import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { makeStyles } from 'design-system/styles';

interface AudioCollectionControlsProps {
  isRecording: boolean;
  isSubmitting: boolean;
  blobLength: number;
  recordedChunks: React.MutableRefObject<Blob[]>;
  handleToggleRecording: () => void;
  handleDeleteAudio: () => void;
  handleSubmitAudio: () => void;
}

const AudioCollectionControls = (props: AudioCollectionControlsProps) => {
  const {
    isRecording,
    isSubmitting,
    blobLength,
    recordedChunks,
    handleToggleRecording,
    handleDeleteAudio,
    handleSubmitAudio,
  } = props;
  const classes = useStyles();

  const isPlayback = !isRecording && blobLength > 0;

  return (
    <div className={classes.wrapper}>
      {!isRecording && !isPlayback && <Empty handleToggleRecording={handleToggleRecording} />}
      {isRecording && <Recording handleToggleRecording={handleToggleRecording} />}
      {isPlayback && (
        <Playback
          recordedChunks={recordedChunks}
          isSubmitting={isSubmitting}
          handleDeleteAudio={handleDeleteAudio}
          handleSubmitAudio={handleSubmitAudio}
        />
      )}
    </div>
  );
};

const Empty = ({ handleToggleRecording }: { handleToggleRecording: () => void }) => {
  const classes = useStyles();
  return (
    <div className={classes.container}>
      <div className={classes.buttonContainer} onClick={handleToggleRecording}>
        <div className={`${classes.buttonInner} ${classes.recordButton}`}>
          <FontAwesomeIcon icon="microphone" />
        </div>
      </div>
    </div>
  );
};

const Recording = ({ handleToggleRecording }: { handleToggleRecording: () => void }) => {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      <div className={classes.buttonContainer} onClick={handleToggleRecording}>
        <div className={`${classes.buttonInner} ${classes.stopButton}`}>
          <FontAwesomeIcon icon="square" />
        </div>
      </div>
    </div>
  );
};

const Playback = ({
  recordedChunks,
  isSubmitting,
  handleDeleteAudio,
  handleSubmitAudio,
}: {
  recordedChunks: React.MutableRefObject<Blob[]>;
  isSubmitting: boolean;
  handleDeleteAudio: () => void;
  handleSubmitAudio: () => void;
}) => {
  const classes = useStyles();
  const audioRef = useRef<HTMLMediaElement>(null);

  const audioBlob = new Blob(recordedChunks.current, { type: recordedChunks.current[0].type });
  const audioSrcUrl = URL.createObjectURL(audioBlob);

  const submitButtonClasses = [classes.buttonContainer];
  if (isSubmitting) {
    submitButtonClasses.push(classes.buttonDisabled);
  }

  return (
    <div className={classes.playback}>
      <div className={classes.audioContainer}>
        <audio ref={audioRef} controls src={audioSrcUrl} />
        <div className={classes.deleteButton} onClick={handleDeleteAudio}>
          <FontAwesomeIcon icon="trash-alt" />
        </div>
      </div>

      <div className={submitButtonClasses.join(' ')} onClick={handleSubmitAudio}>
        <div className={`${classes.buttonInner} ${classes.submitButton}`}>
          <FontAwesomeIcon icon="paper-plane" />
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  wrapper: {
    bottom: 0,
    width: '100%',
    padding: 24,
    background: 'white',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonContainer: {
    borderRadius: 100,
    border: `4px solid ${theme.colors.gray10}`,
    background: theme.colors.white,
    boxShadow: theme.boxShadowLargeTop,
    padding: 8,
    height: 72,
    width: 72,
    display: 'inline-flex',
    alignItems: 'stretch',
    justifyContent: 'stretch',
    cursor: 'pointer',
  },
  buttonInner: {
    flexGrow: 1,
    borderRadius: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,

    '&:active': { transform: 'scale(1.1)' },
    transition: 'all 0.2s',
  },
  buttonDisabled: {
    pointerEvents: 'none',
    opacity: 0.5,
  },
  recordButton: {
    background: theme.colors.red,
    color: theme.colors.white,
  },
  stopButton: {
    color: theme.colors.red,
    fontSize: 24,
  },
  submitButton: {
    background: theme.colors.green,
    color: theme.colors.white,
  },
  playback: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  audioContainer: {
    margin: '16px 0',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gridColumnGap: 16,
    alignItems: 'center',
  },
  deleteButton: {
    flexShrink: 0,
    width: 32,
    height: 32,
    borderRadius: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.gray20,
    fontSize: 16,
    boxShadow: theme.boxShadowLarge,
    cursor: 'pointer',
  },
}));

export default AudioCollectionControls;
